package com.htmlagent.site.pages

import com.htmlagent.site.i18n.asMessage
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.components.sections.landing.EnterDialog
import kotlinx.coroutines.launch
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.widgets.FadingPage
import com.htmlagent.site.components.sections.landing.InstallFab
import com.htmlagent.site.components.sections.landing.LandingAudience
import com.htmlagent.site.components.sections.landing.LandingCta
import com.htmlagent.site.components.sections.landing.LandingFaq
import com.htmlagent.site.components.sections.landing.LandingHero
import com.htmlagent.site.components.sections.landing.LandingNav
import com.htmlagent.site.components.sections.landing.LandingSamples
import com.htmlagent.site.components.sections.landing.LandingStats
import com.htmlagent.site.components.sections.landing.LandingSteps
import com.htmlagent.site.components.sections.landing.SECTION_STEPS
import com.htmlagent.shared.SiteRoutes
import com.htmlagent.site.api.AgentClient
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.Identity
import com.htmlagent.site.state.awaitSignIn
import com.htmlagent.site.state.signedInIdentity
import com.htmlagent.site.theme.LandingContentStyle
import com.htmlagent.site.theme.LandingPageStyle
import com.htmlagent.site.theme.SkipLinkStyle
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.htmlagent.site.theme.ThemeTokensStyle
import com.varabyte.kobweb.core.Page
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/** Where the workspace lives, now that the root belongs to the landing. */
const val APP_ROUTE: String = SiteRoutes.APP

/** What the skip link jumps to: the `<main>` that holds every band of the page. */
private const val CONTENT_ID = "content"

/**
 * The public page: what the product is, and the one door into it.
 *
 * Every call to action is the same decision: whoever is signed in goes straight to the
 * workspace, and whoever is not gets the entry dialog over the page, which asks for an email or
 * lets them go on as a guest in one tap. The form at the foot sends the link at once and opens
 * the dialog on "check your email".
 *
 * The theme tokens are pinned here, on the outermost element, exactly as the workspace does it:
 * the landing is a second front on the same design system, not a second design system.
 */
@Page
@Composable
fun HomePage() {
    val scope = rememberCoroutineScope()
    val client = remember { AgentClient(baseUrl = ApiBaseUrl) }
    var dialog by remember { mutableStateOf<EntryDialog?>(null) }

    // Asked once, on mount: the identity only changes on the way out of this page, and a bar
    // whose label changed under a finger already on the button would be worse than stale.
    var known by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) { known = client.signedInIdentity().getOrNull() != null }

    FadingPage(LandingPageStyle.toModifier().then(ThemeTokensStyle.toModifier())) { leave ->
        fun openApp() = leave(APP_ROUTE)

        // Asked again on the click and not taken from `known`, which may not have arrived yet:
        // opening the dialog over somebody already signed in would offer them a second guest.
        fun start() {
            scope.launch {
                if (client.signedInIdentity().getOrNull() != null) openApp() else dialog = EntryDialog()
            }
        }

        // The dialog opens with the link already on its way, so the foot's form and the
        // dialog's both end on the same "check your email".
        fun enter(email: String) {
            dialog = EntryDialog(sending = true)
            scope.launch {
                dialog = client.requestLink(email, AppLocale.current.value).fold(
                    ifLeft = { EntryDialog(error = it.asMessage()) },
                    ifRight = { EntryDialog(sentTo = email, match = it.match) },
                )
            }
        }

        fun enterAsGuest() {
            scope.launch {
                client.guest().fold(
                    ifLeft = { dialog = EntryDialog(error = it.asMessage()) },
                    ifRight = { openApp() },
                )
            }
        }

        // The first thing a keyboard reaches, and the only thing that skips the bar. Without it
        // every visit starts by tabbing through the mark, five anchors and two switches.
        A(href = "#$CONTENT_ID", attrs = SkipLinkStyle.toModifier().toAttrs()) {
            Text(LocalStrings.current.landing.skipToContent)
        }

        LandingNav(known = known, onStart = ::start)

        EnterDialog(
            visible = dialog != null,
            onEnter = ::enter,
            onGuest = ::enterAsGuest,
            onClose = { dialog = null },
            sending = dialog?.sending == true,
            error = dialog?.error,
            sentTo = dialog?.sentTo,
            match = dialog?.match,
            onOtherEmail = { dialog = EntryDialog() },
        )

        // While the link is on its way, the landing waits for it and goes into the app once it
        // is opened here or approved from another browser.
        val waitingFor = dialog?.match
        LaunchedEffect(waitingFor) {
            if (waitingFor == null) return@LaunchedEffect
            client.awaitSignIn().fold(
                ifLeft = { dialog = EntryDialog(error = it.asMessage()) },
                ifRight = { openApp() },
            )
        }

        Main(LandingContentStyle.toModifier().toAttrs { attr("id", CONTENT_ID) }) {
            LandingHero(onStart = ::start, onLearnMore = { scrollTo(SECTION_STEPS) })
            LandingSteps(onStart = ::start)
            LandingStats()
            LandingAudience()
            LandingSamples()
            LandingFaq()
            LandingCta(onStart = ::start, onEnter = ::enter)
        }

        InstallFab()
    }
}

/**
 * What the entry dialog is showing: the form, the form while sending, a failure, or the link sent
 * with the [match] number to show while waiting for it.
 */
internal data class EntryDialog(
    val sending: Boolean = false,
    val error: String? = null,
    val sentTo: String? = null,
    val match: Int? = null,
)

/**
 * Moves to a band by its anchor.
 *
 * It is a scroll and not a change of location on purpose: writing the fragment into the URL
 * would put a step in the history, and the back button would then walk the person up the page
 * one section at a time instead of taking them where they came from.
 */
private fun scrollTo(section: String) {
    (document.getElementById(section) as? HTMLElement)?.scrollIntoView()
}
